import React, { createContext, useContext, useState, useEffect, useRef, ReactNode, useCallback, useMemo } from 'react';
import { IHighlighter, createHighlighter } from '../utils/highlight';
import { StableElementAddressing, ContentFingerprint, ElementPathStep } from '../utils/stable-element-addressing';
import { revealCollapsedToggleAncestors } from '../utils/toggle-beforematch-reveal.utils';
import {
    findAnyModeMatches,
    tokenizeAnyQuery,
} from '../utils/local-search-match.utils';
import type { LocalSearchMatchMode } from '../utils/local-search-match.utils';


// ============================================================================
// Types and Interfaces
// ============================================================================

interface TextMatch {
    text: string;
    start: number;
    end: number;
}

interface SearchResult {
    blockId: string;
    blockSelector: string;
    blockPath: ElementPathStep[];
    blockFingerprint: ContentFingerprint;
    textStart: number;
    textEnd: number;
    matchText: string;
    resultId: string;
    blockText: string;
    blockHeading: string;
}

interface HighlightMapping {
    resultId: string;
    range: Range;
}

interface SearchHit {
    id: string;
    text: string;
    blockContent: string;
    blockHeading: string;
    element: HTMLElement | null;
    startOffset: number;
    endOffset: number;
}

// ============================================================================
// Main React-Safe Search System
// ============================================================================

class ReactSafeSearchSystem {
    private addressing: StableElementAddressing;
    private currentResults: SearchResult[] = [];
    private highlighter: IHighlighter;
    private mutationObserver: MutationObserver | null = null;
    private rootElement: HTMLElement;
    private currentSearchHitIndex: number = 0;
    private isDestroyed: boolean = false;
    private lastSearchParams: { term: string; caseSensitive: boolean; wholeWord: boolean; match: LocalSearchMatchMode } | null = null;
    private contentBlockSelector: string;
    private onSearchResultsChanged: ((hits: SearchHit[]) => void) | null = null;
    private revealEpoch = 0;
    private pendingRevealFrame: number | null = null;
    private pendingRevealTimeout: ReturnType<typeof setTimeout> | null = null;

    /**
     * Initializes a new ReactSafeSearchSystem instance.
     * Sets up the search system with stable element addressing, highlighting, and DOM mutation observation.
     * 
     * @param rootElement - The root HTML element to search within
     * @param contentBlockSelector - CSS selector for content blocks to search in (e.g., 'p, h1, h2, h3, h4, h5, h6, li, td')
     */
    constructor(rootElement: HTMLElement, contentBlockSelector: string) {
        this.rootElement = rootElement;
        this.addressing = new StableElementAddressing(rootElement, contentBlockSelector);
        this.highlighter = createHighlighter();
        this.setupMutationObserver();
        this.contentBlockSelector = contentBlockSelector;
    }

    /**
     * Sets a callback function to be called when search results change.
     * This allows the React context to be notified of state changes.
     * 
     * @param callback - Function to call when search results change
     */
    public setSearchResultsCallback(callback: (hits: SearchHit[]) => void): void {
        this.onSearchResultsChanged = callback;
    }

    /**
     * Sets the current search hit index and updates the UI accordingly.
     * Reveals collapsed ToggleSwitch ancestors of the current hit via synthetic `beforematch`,
     * then refreshes highlights and scrolls the hit into view after layout.
     *
     * @param index - The index of the search hit to make current (0-based)
     */
    public setCurrentSearchHitIndex(index: number): void {
        this.currentSearchHitIndex = index;
        this.cancelPendingReveal();

        const result = this.currentResults[index];
        if (!result) {
            this.updateHighlights();
            return;
        }

        const element = this.addressing.resolveElement({
            selector: result.blockSelector,
            elementPath: result.blockPath,
            fingerprint: result.blockFingerprint
        }, this.rootElement);

        if (revealCollapsedToggleAncestors(element)) {
            this.scheduleHighlightAndScroll(element);
            return;
        }

        this.updateHighlights();
        this.scrollToElement(element);
    }

    /**
     * Sets up a MutationObserver to detect DOM changes and handle React re-renders.
     * Observes changes to child elements, subtree modifications, and text content changes.
     * Uses debouncing to prevent excessive re-processing during rapid DOM updates.
     */
    private setupMutationObserver(): void {
        this.mutationObserver = new MutationObserver((_mutations) => {
            // Debounce DOM updates
            this.debouncedHandleReactRerender();
        });

        this.mutationObserver.observe(this.rootElement, {
            childList: true,
            subtree: true,
            attributes: false,
            characterData: true,

        });
    }

    private debouncedHandleReactRerender = this.debounce(() => {
        this.handleReactRerender();
    }, 100);

    /**
     * Creates a debounced version of a function to prevent excessive calls.
     * The debounced function will only execute after the specified wait time has passed
     * since the last time it was invoked.
     * 
     * @param func - The function to debounce
     * @param wait - The number of milliseconds to wait before executing the function
     * @returns A debounced version of the input function
     */
    private debounce<T extends (...args: any[]) => void>(func: T, wait: number): T {
        let timeout: ReturnType<typeof setTimeout>;
        return ((...args: any[]) => {
            clearTimeout(timeout);
            if (this.isDestroyed) return;
            timeout = setTimeout(() => func.apply(this, args), wait);
        }) as T;
    }

    /**
     * Performs a search operation across all content blocks.
     * Searches for the given term with specified options and updates highlights.
     * 
     * @param searchTerm - The text to search for
     * @param caseSensitive - Whether the search should be case-sensitive
     * @param wholeWord - Whether to match only whole words
     * @param match - How to interpret `searchTerm`: `"exact"` (default) is one substring; `"any"` is OR of tokens/phrases
     * @returns Array of SearchResult objects containing all matches found
     */
    public performSearch(
        searchTerm: string,
        caseSensitive: boolean,
        wholeWord: boolean,
        match: LocalSearchMatchMode = "exact",
    ): SearchResult[] {
        if (!searchTerm.trim()) {
            this.currentResults = [];
            this.highlighter.clearHighlights();
            this.lastSearchParams = null;
            // Notify React context of cleared results
            if (this.onSearchResultsChanged) {
                this.onSearchResultsChanged([]);
            }
            return [];
        }

        // Store search parameters for potential re-execution
        this.lastSearchParams = { term: searchTerm, caseSensitive, wholeWord, match };

        // Content-based search (no DOM references)
        const contentResults = this.searchInContent(searchTerm, caseSensitive, wholeWord, match);
        this.currentResults = contentResults;

        // Highlight current DOM
        this.updateHighlights();

        // Notify React context of new results
        if (this.onSearchResultsChanged) {
            const hits = this.getCurrentSearchHits();
            this.onSearchResultsChanged(hits);
        }

        return contentResults;
    }

    /**
     * Searches for text matches within all content blocks using stable addressing.
     * Processes each content block to find matches and creates SearchResult objects.
     * 
     * @param searchTerm - The text to search for
     * @param caseSensitive - Whether the search should be case-sensitive
     * @param wholeWord - Whether to match only whole words
     * @param match - Exact substring vs OR of tokens/quoted phrases
     * @returns Array of SearchResult objects with match details and stable element references
     */
    private searchInContent(
        searchTerm: string,
        caseSensitive: boolean,
        wholeWord: boolean,
        match: LocalSearchMatchMode,
    ): SearchResult[] {
        const results: SearchResult[] = [];
        const anyAtoms = match === "any" ? tokenizeAnyQuery(searchTerm, caseSensitive) : null;
        const escapedTerm = wholeWord ? `\\b${this.escapeRegex(searchTerm)}\\b` : this.escapeRegex(searchTerm);

        this.addressing.contentBlocks.forEach((block) => {
            const matches = anyAtoms
                ? findAnyModeMatches(block.text, anyAtoms, caseSensitive, wholeWord)
                : this.findTextMatches(block.text, escapedTerm, caseSensitive);

            matches.forEach((textMatch, matchIndex) => {
                results.push({
                    blockId: block.id,
                    blockSelector: block.selector,
                    blockPath: block.elementPath,
                    blockFingerprint: block.fingerprint,
                    textStart: textMatch.start,
                    textEnd: textMatch.end,
                    matchText: textMatch.text,
                    resultId: `${block.id}-${matchIndex}`,
                    blockText: block.text,
                    blockHeading: block.heading || ''
                });
            });

        });

        return results;
    }

    /**
     * Finds all text matches in a given string using regular expressions.
     * Handles case sensitivity and prevents infinite loops on zero-length matches.
     * 
     * @param text - The text to search within
     * @param escapedSearchTerm - The escaped search term (already processed for regex)
     * @param caseSensitive - Whether the search should be case-sensitive
     * @returns Array of TextMatch objects with match text and position information
     */
    private findTextMatches(text: string, escapedSearchTerm: string, caseSensitive: boolean): TextMatch[] {
        const matches: TextMatch[] = [];
        const regex = new RegExp(escapedSearchTerm, caseSensitive ? 'g' : 'gi');
        let match: RegExpExecArray | null;

        while ((match = regex.exec(text)) !== null) {
            matches.push({
                text: match[0],
                start: match.index,
                end: match.index + match[0].length
            });

            // Prevent infinite loop on zero-length matches
            if (match[0].length === 0) {
                regex.lastIndex++;
            }
        }

        return matches;
    }

    /**
     * Escapes special regex characters in a string to make it safe for use in regular expressions.
     * Prevents regex injection and ensures literal matching of the input string.
     * 
     * @param string - The string to escape
     * @returns The escaped string safe for use in regex patterns
     */
    private escapeRegex(string: string): string {
        return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }

    /**
     * Handles React re-renders by refreshing the addressing system and re-executing searches.
     * Called when DOM mutations are detected to maintain search accuracy after React updates.
     * Re-runs the last search if one was active, otherwise just updates highlights.
     */
    public handleReactRerender(): void {
        // Refresh addressing system with current DOM
        this.addressing.refresh(this.rootElement, this.contentBlockSelector);

        // Re-run search if there was an active search to recalculate hits
        if (this.lastSearchParams) {
            // Use setTimeout to ensure addressing refresh is complete
            setTimeout(() => {
                this.performSearch(
                    this.lastSearchParams!.term,
                    this.lastSearchParams!.caseSensitive,
                    this.lastSearchParams!.wholeWord,
                    this.lastSearchParams!.match,
                );
            }, 0);
        } else {
            // Just update highlights if no active search
            this.updateHighlights();
        }
    }

    /**
     * Cancels a pending post-reveal highlight/scroll so a newer hit cannot be overtaken by a stale frame.
     */
    private cancelPendingReveal(): void {
        this.revealEpoch += 1;
        if (this.pendingRevealFrame !== null) {
            cancelAnimationFrame(this.pendingRevealFrame);
            this.pendingRevealFrame = null;
        }
        if (this.pendingRevealTimeout !== null) {
            clearTimeout(this.pendingRevealTimeout);
            this.pendingRevealTimeout = null;
        }
    }

    /**
     * After synthetic `beforematch`, wait for React state, `useEffect` (`hidden` removal), and paint
     * so highlight ranges have real layout boxes before scrolling.
     */
    private scheduleHighlightAndScroll(element: HTMLElement | null): void {
        this.cancelPendingReveal();
        const epoch = this.revealEpoch;
        this.pendingRevealFrame = requestAnimationFrame(() => {
            this.pendingRevealFrame = null;
            this.pendingRevealTimeout = setTimeout(() => {
                this.pendingRevealTimeout = null;
                if (this.isDestroyed || epoch !== this.revealEpoch) {
                    return;
                }
                this.updateHighlights();
                this.scrollToElement(element);
            }, 0);
        });
    }

    /**
     * Scrolls the specified element into view with configurable options.
     * Uses centered positioning by default. Does not reveal collapsed ToggleSwitch content;
     * callers must dispatch `beforematch` and wait for layout first when ancestors are collapsed.
     *
     * @param element - The HTML element to scroll into view (null-safe)
     * @param options - Optional scroll behavior configuration to override defaults
     */
    private scrollToElement(
        element: HTMLElement | null,
        options: ScrollIntoViewOptions = {}
    ): void {
        const defaultOptions: ScrollIntoViewOptions = {
            // behavior: 'smooth', // Smooth scrolling gets interrupted by React rendering in some cases
            block: 'center',
            inline: 'center',

        };

        element?.scrollIntoView?.({
            ...defaultOptions,
            ...options
        });
    }

    /**
     * Updates visual highlights for all current search results.
     * Resolves elements using stable addressing, creates text ranges, and applies highlighting.
     * Handles cases where elements cannot be resolved or ranges cannot be created.
     */
    private updateHighlights(): void {
        const highlights: HighlightMapping[] = [];

        for (const result of this.currentResults) {
            // Resolve element from stable address
            const element: HTMLElement | null = this.addressing.resolveElement({
                selector: result.blockSelector,
                elementPath: result.blockPath,
                fingerprint: result.blockFingerprint
            }, this.rootElement);

            if (element) {
                // Create text range within resolved element
                const range = this.createTextRange(
                    element,
                    result.textStart,
                    result.textEnd,
                    result.matchText
                );

                if (range) {
                    highlights.push({
                        resultId: result.resultId,
                        range
                    });
                } else {
                    console.log('Failed to create range for result:', result.resultId);
                }
            } else {
                console.log('Failed to resolve element for result:', result.resultId);
            }
        }

        // Update visual highlights
        this.highlighter.highlightRanges(highlights.map(h => h.range), this.currentSearchHitIndex);
    }

    /**
     * Creates a DOM Range object for highlighting text within an element.
     * Handles the mapping between trimmed text positions (used in search) and raw DOM positions.
     * Uses TreeWalker to find the correct text nodes and creates precise ranges for highlighting.
     * 
     * @param element - The HTML element containing the text
     * @param textStart - Start position in the trimmed text content
     * @param textEnd - End position in the trimmed text content
     * @param expectedText - The expected text content to verify the range
     * @returns A DOM Range object for the specified text, or null if not found
     */
    private createTextRange(
        element: HTMLElement,
        textStart: number,
        textEnd: number,
        expectedText: string
    ): Range | null {
        // Get the trimmed text content to match what was used in content blocks
        const elementText = element.textContent?.trim() || '';

        // Check if the expected text is within the trimmed text
        if (textStart >= 0 && textEnd <= elementText.length) {
            const actualText = elementText.substring(textStart, textEnd);

            if (actualText.toLowerCase() === expectedText.toLowerCase()) {
                // Map trimmed text positions to raw DOM positions
                const rawText = element.textContent || '';
                const trimmedStart = this.findTrimmedPositionInRawText(rawText, textStart);
                const trimmedEnd = this.findTrimmedPositionInRawText(rawText, textEnd);

                if (trimmedStart !== -1 && trimmedEnd !== -1) {
                    // Find the corresponding text nodes in the DOM
                    const walker = document.createTreeWalker(
                        element,
                        NodeFilter.SHOW_TEXT,
                        {
                            acceptNode: (node) => {
                                const parent = node.parentElement;
                                return parent && !['SCRIPT', 'STYLE'].includes(parent.tagName)
                                    ? NodeFilter.FILTER_ACCEPT
                                    : NodeFilter.FILTER_REJECT;
                            }
                        }
                    );

                    let currentPos = 0;
                    let node: Text | null;

                    while ((node = walker.nextNode() as Text)) {
                        const text = node.textContent || '';
                        const nodeEnd = currentPos + text.length;

                        if (currentPos <= trimmedStart && trimmedEnd <= nodeEnd) {
                            // Match is within this text node
                            try {
                                const range = document.createRange();
                                const startOffset = trimmedStart - currentPos;
                                const endOffset = trimmedEnd - currentPos;

                                range.setStart(node, startOffset);
                                range.setEnd(node, endOffset);

                                // Verify content matches
                                const rangeText = range.toString().toLowerCase();

                                if (rangeText === expectedText.toLowerCase()) {
                                    return range;
                                }
                            } catch (error) {
                                console.warn('Failed to create range:', error);
                            }
                        }

                        currentPos = nodeEnd;
                    }
                }
            }
        }

        return null; // Match not found or content changed
    }

    /**
     * Maps a position in trimmed text to the corresponding position in raw text.
     * Handles the conversion between search positions (based on trimmed content) and DOM positions.
     * Skips leading and trailing whitespace to match the trimming behavior used in content blocks.
     * 
     * @param rawText - The original text content with all whitespace
     * @param trimmedPosition - The position in the trimmed version of the text
     * @returns The corresponding position in the raw text, or -1 if not found
     */
    private findTrimmedPositionInRawText(rawText: string, trimmedPosition: number): number {
        let rawPos = 0;
        let trimmedPos = 0;

        for (let i = 0; i < rawText.length; i++) {
            const char = rawText[i];

            // Skip leading whitespace
            if (trimmedPos === 0 && /\s/.test(char)) {
                rawPos++;
                continue;
            }

            // If we've reached the target trimmed position
            if (trimmedPos === trimmedPosition) {
                return rawPos;
            }

            // Skip trailing whitespace at the end
            if (trimmedPos > 0 && /\s/.test(char)) {
                const remainingText = rawText.substring(i);
                if (remainingText.trim() === '') {
                    break;
                }
            }

            rawPos++;
            trimmedPos++;
        }

        return trimmedPos === trimmedPosition ? rawPos : -1;
    }

    /**
     * Returns the current search hits as SearchHit objects with resolved DOM elements.
     * Converts internal SearchResult objects to the public SearchHit interface.
     * Resolves DOM elements using stable addressing for each search result.
     * 
     * @returns Array of SearchHit objects containing match information and resolved elements
     */
    public getCurrentSearchHits(): SearchHit[] {
        return this.currentResults.map((result) => {
            const element = this.addressing.resolveElement({
                selector: result.blockSelector,
                elementPath: result.blockPath,
                fingerprint: result.blockFingerprint
            }, this.rootElement);

            return {
                id: result.resultId,
                text: result.matchText,
                blockContent: result.blockText,
                blockHeading: result.blockHeading,
                element: element || null,
                startOffset: result.textStart,
                endOffset: result.textEnd
            };
        });
    }

    /**
     * Clears all search results and removes visual highlights.
     * Resets the search state to prepare for a new search operation.
     */
    public clearSearch(): void {
        this.cancelPendingReveal();
        this.currentResults = [];
        this.highlighter.clearHighlights();
        this.lastSearchParams = null;
        // Notify React context of cleared results
        if (this.onSearchResultsChanged) {
            // this.onSearchResultsChanged([]);
        }
    }

    /**
     * Destroys the search system and cleans up all resources.
     * Disconnects the mutation observer, destroys the highlighter, and marks the system as destroyed.
     * Should be called when the search system is no longer needed to prevent memory leaks.
     */
    public destroy(): void {
        this.cancelPendingReveal();
        if (this.mutationObserver) {
            this.mutationObserver.disconnect();
            this.mutationObserver = null;
        }
        this.highlighter.destroy();
        this.currentResults = [];
        this.isDestroyed = true;
    }

    /**
     * Handles viewport changes (scroll/resize events) by updating highlight positions.
     * Only updates highlights if there are active search results to avoid unnecessary work.
     * Called automatically by the React context when viewport changes are detected.
     */
    public handleViewportChange(): void {
        if (this.currentResults.length > 0) {
            this.highlighter.updateHighlightPositions(this.currentSearchHitIndex);
        }
    }
}

// ============================================================================
// React Context Integration
// ============================================================================

interface SearchContextValue {
    searchTerm: string;
    searchHits: SearchHit[];
    currentSearchHitIndex: number;
    setCurrentSearchHitIndex: (index: number) => void;
    performSearch: (term: string, caseSensitive: boolean, wholeWord: boolean, match?: LocalSearchMatchMode) => void;
    clearSearch: () => void;
    goToNextHit: () => void;
    goToPreviousHit: () => void;
    goToHit: (index: number) => void;
    isSearching: boolean;
}

const SearchContext = createContext<SearchContextValue | undefined>(undefined);

interface SearchProviderProps {
    children: ReactNode;
    contentBlockSelector?: string;
}

/**
 * React context provider for search functionality.
 * Manages search state, DOM observation, and provides search methods to child components.
 * Automatically handles viewport changes and React re-renders to maintain search accuracy.
 * Binds to the first outlet element in DOM order whose data-search attribute is not "none"
 * (i.e. the first `.__outlet:not([data-search="none"])` element).
 * 
 * @param children - React children components that will have access to search context
 * @param contentBlockSelector - CSS selector for content blocks to search in
 */
export const SearchProvider: React.FC<SearchProviderProps> = ({
    children,
    contentBlockSelector = 'p, h1, h2, h3, h4, h5, h6, li, td',
}) => {
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [searchHits, setSearchHits] = useState<SearchHit[]>([]);
    const [currentSearchHitIndex, setCurrentSearchHitIndex] = useState<number>(0);
    const [isSearching, setIsSearching] = useState<boolean>(false);
    const searchSystemRef = useRef<ReactSafeSearchSystem | null>(null);
    const viewportChangeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        let animationFrameId: number | null = null;
        let isActive = true;

        const contentElement = document.querySelector<HTMLElement>('.__outlet:not([data-search="none"])');
        if (contentElement) {
            searchSystemRef.current = new ReactSafeSearchSystem(contentElement, contentBlockSelector);
            // Set up callback to update React state when search results change
            searchSystemRef.current.setSearchResultsCallback((hits: SearchHit[]) => {
                setSearchHits(hits);
            });
        } else {
            console.warn('No searchable outlet found: no .__outlet element without data-search="none"');
        }

        // Handle scroll and resize events with debouncing
        const handleViewportChange = () => {
            if (viewportChangeTimeoutRef.current) {
                clearTimeout(viewportChangeTimeoutRef.current);
            }

            viewportChangeTimeoutRef.current = setTimeout(() => {
                if (searchSystemRef.current) {
                    searchSystemRef.current.handleViewportChange();
                }
            }, 16); // ~60fps
        };

        // Use requestAnimationFrame to detect scroll changes on any element
        let lastScrollPositions = new Map();
        let scrollableElements: HTMLElement[] = [];

        // Find all scrollable elements once
        const findScrollableElements = () => {
            scrollableElements = [];
            const allElements = document.querySelectorAll('*');
            allElements.forEach((el) => {
                const style = window.getComputedStyle(el);
                if (style.overflow === 'auto' || style.overflow === 'scroll' ||
                    style.overflowY === 'auto' || style.overflowY === 'scroll' ||
                    style.overflowX === 'auto' || style.overflowX === 'scroll') {
                    scrollableElements.push(el as HTMLElement);
                }
            });
        };

        // Find scrollable elements initially and after a delay to catch dynamically added ones
        findScrollableElements();
        setTimeout(findScrollableElements, 1000);

        const checkAllScrollPositions = () => {
            if (!isActive) return;

            // Only check if there are active highlights
            if (!searchSystemRef.current || searchSystemRef.current['currentResults'].length === 0) {
                animationFrameId = requestAnimationFrame(checkAllScrollPositions);
                return;
            }

            let scrollDetected = false;

            // Check window scroll
            const windowScrollX = window.scrollX;
            const windowScrollY = window.scrollY;
            const windowKey = 'window';

            if (!lastScrollPositions.has(windowKey) ||
                lastScrollPositions.get(windowKey).x !== windowScrollX ||
                lastScrollPositions.get(windowKey).y !== windowScrollY) {
                lastScrollPositions.set(windowKey, { x: windowScrollX, y: windowScrollY });
                scrollDetected = true;
            }

            // Check content element scroll
            if (contentElement) {
                const contentScrollTop = contentElement.scrollTop;
                const contentScrollLeft = contentElement.scrollLeft;
                const contentKey = 'content';

                if (!lastScrollPositions.has(contentKey) ||
                    lastScrollPositions.get(contentKey).top !== contentScrollTop ||
                    lastScrollPositions.get(contentKey).left !== contentScrollLeft) {
                    lastScrollPositions.set(contentKey, { top: contentScrollTop, left: contentScrollLeft });
                    scrollDetected = true;
                }
            }

            // Check known scrollable elements
            scrollableElements.forEach((el) => {
                const scrollTop = el.scrollTop;
                const scrollLeft = el.scrollLeft;
                const elementKey = `element-${el.tagName}-${el.id || el.className}`;

                if (!lastScrollPositions.has(elementKey) ||
                    lastScrollPositions.get(elementKey).top !== scrollTop ||
                    lastScrollPositions.get(elementKey).left !== scrollLeft) {
                    lastScrollPositions.set(elementKey, { top: scrollTop, left: scrollLeft });
                    scrollDetected = true;
                }
            });

            if (scrollDetected) {
                // Remove debouncing for immediate response
                if (searchSystemRef.current) {
                    searchSystemRef.current.handleViewportChange();
                }
            }

            animationFrameId = requestAnimationFrame(checkAllScrollPositions);
        };

        animationFrameId = requestAnimationFrame(checkAllScrollPositions);

        window.addEventListener('scroll', handleViewportChange, { passive: true });
        window.addEventListener('resize', handleViewportChange, { passive: true });

        return () => {
            isActive = false;
            if (animationFrameId) {
                cancelAnimationFrame(animationFrameId);
            }
            window.removeEventListener('scroll', handleViewportChange);
            window.removeEventListener('resize', handleViewportChange);
            if (viewportChangeTimeoutRef.current) {
                clearTimeout(viewportChangeTimeoutRef.current);
            }
            if (searchSystemRef.current) {
                searchSystemRef.current.destroy();
            }
        };
    }, [contentBlockSelector]);

    useEffect(() => {
        if (searchSystemRef.current) {
            searchSystemRef.current.setCurrentSearchHitIndex(currentSearchHitIndex);
        }
    }, [currentSearchHitIndex]);

    /**
     * Performs a search operation with debouncing to prevent excessive API calls.
     * Updates search state and triggers the underlying search system.
     * 
     * @param term - The search term to look for
     * @param caseSensitive - Whether the search should be case-sensitive
     * @param wholeWord - Whether to match only whole words
     * @param match - How to interpret `term`; defaults to `"exact"` so existing call sites keep compiling
     */
    const performSearch = useCallback((term: string, caseSensitive: boolean, wholeWord: boolean, match: LocalSearchMatchMode = "exact") => {
        // Debounce search execution
        setTimeout(() => {
            if (!searchSystemRef.current) {
                console.warn('Search system not available');
                return;
            }

            setIsSearching(true);
            setSearchTerm(term);
            setCurrentSearchHitIndex(0); // Reset to first hit when new search is performed

            if (searchSystemRef.current) {
                searchSystemRef.current.performSearch(term, caseSensitive, wholeWord, match);
                const hits = searchSystemRef.current.getCurrentSearchHits();

                // Force scroll to first hit on new search (React state may not change if index was already 0)
                searchSystemRef.current.setCurrentSearchHitIndex(0);

                searchSystemRef.current.handleViewportChange();

                setSearchHits(hits);
                setIsSearching(false);
            }
        }, 150);
    }, []);

    /**
     * Clears the current search and resets all search-related state.
     * Removes highlights and prepares the system for a new search.
     */
    const clearSearch = () => {
        if (searchSystemRef.current) {
            searchSystemRef.current.clearSearch();
        }
        setSearchTerm('');
        setSearchHits([]);
        setCurrentSearchHitIndex(0);
        setIsSearching(false);
    };

    /**
     * Navigates to the next search hit in the results.
     * Wraps around to the first hit when reaching the end of the results.
     */
    const goToNextHit = () => {
        if (searchHits.length > 0) {
            setCurrentSearchHitIndex((prev) => (prev + 1) % searchHits.length);
        }
    };

    /**
     * Navigates to the previous search hit in the results.
     * Wraps around to the last hit when reaching the beginning of the results.
     */
    const goToPreviousHit = () => {
        if (searchHits.length > 0) {
            setCurrentSearchHitIndex((prev) => (prev - 1 + searchHits.length) % searchHits.length);
        }
    };

    /**
     * Navigates directly to a specific search hit by index.
     * Only changes the current hit if the index is valid.
     * 
     * @param index - The index of the search hit to navigate to (0-based)
     */
    const goToHit = (index: number) => {
        if (index >= 0 && index < searchHits.length) {
            if (index === currentSearchHitIndex) {
                // Same index: force scroll into view (React state won't change, so useEffect won't run)
                searchSystemRef.current?.setCurrentSearchHitIndex(index);
            } else {
                setCurrentSearchHitIndex(index);
            }
        }
    };

    const contextValue: SearchContextValue = {
        searchTerm,
        searchHits,
        currentSearchHitIndex,
        setCurrentSearchHitIndex,
        performSearch,
        clearSearch,
        goToNextHit,
        goToPreviousHit,
        goToHit,
        isSearching,
    };

    return (
        <SearchContext.Provider value={contextValue}>
            {children}
        </SearchContext.Provider>
    );
};

// ============================================================================
// React Hooks
// ============================================================================

/**
 * React hook to access the search context.
 * Provides access to all search functionality including state and methods.
 * Preferably use the specialized hooks instead of this one to avoid unnecessary re-renders.
 * Must be used within a SearchProvider component.
 * 
 * @returns The search context value containing state and methods
 * @throws Error if used outside of a SearchProvider
 */
export const useSearchContext = (): SearchContextValue => {
    const context = useContext(SearchContext);
    if (!context) {
        throw new Error('useSearchContext must be used within a SearchProvider');
    }
    return context;
};

/**
 * React hook to access only the current search hits.
 * A convenience hook that extracts just the search hits from the search context.
 * Must be used within a SearchProvider component.
 * 
 * @returns Array of current search hits
 */
export const useSearchHits = (): SearchHit[] => {
    const { searchHits } = useSearchContext();
    return useMemo(() => searchHits, [searchHits]);
};

/**
 * React hook to access only the current search hit index.
 * A convenience hook that extracts just the current search hit index from the search context.
 * Must be used within a SearchProvider component.
 * 
 * @returns The current search hit index
 */
export const useCurrentSearchHitIndex = (): number => {
    const { currentSearchHitIndex } = useSearchContext();
    return useMemo(() => currentSearchHitIndex, [currentSearchHitIndex]);
};

/**
 * React hook to access only the performSearch function.
 * A convenience hook that extracts just the performSearch function from the search context.
 * Must be used within a SearchProvider component.
 * 
 * @returns The performSearch function
 */
export const usePerformSearch = (): ((term: string, caseSensitive: boolean, wholeWord: boolean, match?: LocalSearchMatchMode) => void) => {
    const { performSearch } = useSearchContext();
    return useMemo(() => performSearch, [performSearch]);
};

/**
 * React hook to access only the goToHit function.
 * A convenience hook that extracts just the goToHit function from the search context.
 * Must be used within a SearchProvider component.
 * 
 * @returns The goToHit function
 */
export const useGoToHit = (): (index: number) => void => {
    const { goToHit } = useSearchContext();
    return useMemo(() => goToHit, [goToHit]);
};

// ============================================================================
// Exports
// ============================================================================

export type {
    SearchHit,
    SearchResult,
    LocalSearchMatchMode,
};

export {
    ReactSafeSearchSystem,
};